import React from 'react';
import PropTypes from 'prop-types';
import { Bookmark, EyeOff, Edit2, Trash2 } from 'lucide-react';
import { getContainerColor } from '../utils/colors';

const Bookmarks = ({ bookmarks, onJumpToLog, onEditBookmark, onRemoveBookmark, filteredLogs }) => {
  // Create a set of visible log IDs for quick lookup
  const visibleLogIds = new Set(filteredLogs.map(log => log.id));

  return (
    <div className="w-80 bg-slate-800 border-l border-slate-700 flex flex-col shrink-0 transition-all duration-300">
      <div className="p-3 border-b border-slate-700 font-semibold flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Bookmark size={16} className="text-yellow-500" />
          <span>Bookmarks</span>
        </div>
        <span className="bg-slate-700 text-xs px-2 py-0.5 rounded-full text-slate-300">
          {bookmarks.length}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
        {bookmarks.length === 0 ? (
          <div className="text-xs text-slate-500 text-center mt-4">
            Click the gutter on a log line to bookmark it.
          </div>
        ) : (
          bookmarks.map(bookmark => {
            const isVisible = visibleLogIds.has(bookmark.logId);
            return (
              <div
                key={bookmark.id}
                className={`p-2 rounded border transition group relative ${
                  isVisible
                    ? 'bg-slate-700/50 border-slate-600 hover:bg-slate-700'
                    : 'bg-slate-700/30 border-orange-600/50 hover:bg-slate-700/40'
                }`}
              >
                <div
                  onClick={() => onJumpToLog(bookmark.logId)}
                  className="cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-[10px] font-mono text-slate-400">
                      {bookmark.timestamp.split('T')[1]?.replace('Z','')}
                    </span>
                    <div className="flex items-center gap-1">
                      {!isVisible && (
                        <EyeOff size={10} className="text-orange-500" title="Hidden by filters" />
                      )}
                      <span className={`text-[10px] ${getContainerColor(bookmark.container)}`}>
                        {bookmark.container}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-white line-clamp-2 mb-1">
                    {bookmark.note ? (
                      <span className="italic">"{bookmark.note}"</span>
                    ) : (
                      <span className="text-slate-300">{bookmark.message}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate font-mono flex items-center gap-1">
                    Line #{bookmark.logId}
                    {!isVisible && (
                      <span className="text-orange-500 text-[9px]">(filtered out)</span>
                    )}
                  </div>
                </div>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditBookmark(bookmark);
                    }}
                    className="p-1 bg-slate-600 hover:bg-blue-600 rounded text-white"
                    title="Edit note"
                  >
                    <Edit2 size={10} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveBookmark(bookmark.logId);
                    }}
                    className="p-1 bg-slate-600 hover:bg-red-600 rounded text-white"
                    title="Remove bookmark"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

Bookmarks.propTypes = {
  bookmarks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    logId: PropTypes.number.isRequired,
    note: PropTypes.string,
    message: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
    container: PropTypes.string.isRequired
  })).isRequired,
  onJumpToLog: PropTypes.func.isRequired,
  onEditBookmark: PropTypes.func.isRequired,
  onRemoveBookmark: PropTypes.func.isRequired,
  filteredLogs: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default Bookmarks;

